import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
} from '@mui/material';
import { Add, Delete, Edit, WarningAmberOutlined, CheckCircleOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  SubscriptionAddonDto,
  useAdminAddonsQuery,
  useCreateAdminAddonMutation,
  useUpdateAdminAddonMutation,
  useDeleteAdminAddonMutation,
} from '../../API/Subscriptions';
import { useModalDisclosure } from '@hooks/useDisclosure';
import { failed, success, warning } from '@components/Common/Toastify';
import StripeCatalogField, { StripeLinkCell } from './StripeCatalogField';
import PriceField from './PriceField';

/** Kept in step with the server's SUBSCRIPTION_ALLOWED_CURRENCIES. See Plans.tsx. */
const CURRENCIES = ['AUD', 'USD', 'NZD', 'GBP', 'EUR', 'CAD', 'SGD'];

/** See the same helper in Plans.tsx — Stripe's non-error consequences must be shown. */
const showStripeWarnings = (res: any) => {
  const warnings: string[] = res?.stripeWarnings || [];
  warnings.forEach((text) => warning(text));
};

const emptyForm: Partial<SubscriptionAddonDto> = {
  code: '',
  name: '',
  price: 0,
  currency: 'AUD',
  type: 'other',
  quantity: null,
  stripePriceId: null,
  isActive: true,
};

const Addons = () => {
  const { data, isLoading, error, refetch } = useAdminAddonsQuery({
    config: { keepPreviousData: true },
  });

  const addons: SubscriptionAddonDto[] = useMemo(() => data?.data || [], [data?.data]);

  const createModal = useModalDisclosure();
  const editModal = useModalDisclosure();
  const toggleConfirmModal = useModalDisclosure();

  const [form, setForm] = useState<Partial<SubscriptionAddonDto>>(emptyForm);

  const createMutation = useCreateAdminAddonMutation({
    onSuccess: (res: any) => {
      success(res?.message || 'Addon created');
      showStripeWarnings(res);
      refetch();
      createModal.close();
      setForm(emptyForm);
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to create addon');
    },
  });

  const updateMutation = useUpdateAdminAddonMutation({
    onSuccess: (res: any) => {
      success(res?.message || 'Addon updated');
      showStripeWarnings(res);
      refetch();
      editModal.close();
      setForm(emptyForm);
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to update addon');
    },
  });

  const deleteMutation = useDeleteAdminAddonMutation({
    onSuccess: (res: any) => {
      success(res?.message || 'Addon successfully deleted / archived');
      refetch();
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to delete addon');
    },
  });

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to safe-delete/archive this addon? Existing purchases will not be affected, but no new purchases can be made.')) {
      deleteMutation.mutate({ id });
    }
  };

  const openCreate = () => {
    setForm(emptyForm);
    createModal.open(null);
  };

  const openEdit = (addon: SubscriptionAddonDto) => {
    setForm({ ...addon });
    editModal.open(addon);
  };

  const onChange = (key: keyof SubscriptionAddonDto, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitCreate = () => {
    createMutation.mutate(form);
  };

  const submitUpdate = () => {
    const addon = editModal.data as SubscriptionAddonDto;
    updateMutation.mutate({ id: addon.id, data: form });
  };

  const editingAddon = editModal.data as SubscriptionAddonDto | null;

  // Only amount and currency are baked into a Stripe price, so only those force
  // a replacement one to be minted.
  const willReprice =
    !!editingAddon &&
    (Number(form.price ?? 0) !== Number(editingAddon.price ?? 0) ||
      String(form.currency || '').toLowerCase() !== String(editingAddon.currency || '').toLowerCase());

  const handleToggleActive = (addon: SubscriptionAddonDto) => {
    toggleConfirmModal.open(addon);
  };

  const confirmToggleActive = () => {
    const addon = toggleConfirmModal.data as SubscriptionAddonDto;
    updateMutation.mutate(
      { id: addon.id, data: { isActive: !addon.isActive } },
      {
        onSuccess: () => {
          toggleConfirmModal.close();
        },
      }
    );
  };

  if (error) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load subscription addons.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2>Optional Add-ons & Top-ups</h2>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Create Addon
          </Button>
        </div>

        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading addons...</Typography>
            </Box>
          ) : addons.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No addons found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Stripe</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addons.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.code}</TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>{a.currency} {a.price}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{a.type}</TableCell>
                    <TableCell>{a.quantity === null ? '-' : a.quantity}</TableCell>
                    <TableCell>
                      <StripeLinkCell amount={a.price} stripePriceId={a.stripePriceId} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!a.isActive}
                        onChange={() => handleToggleActive(a)}
                        disabled={updateMutation.isLoading}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{a.updatedAt ? dayjs(a.updatedAt).format('MMM DD, YYYY') : '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => openEdit(a)} startIcon={<Edit />}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDelete(a.id)} startIcon={<Delete />} disabled={deleteMutation.isLoading}>
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Create dialog */}
        <Dialog open={createModal.isOpen} onClose={createModal.close} maxWidth="md" fullWidth>
          <DialogTitle>Create Addon Plan</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
              <TextField label="Code" value={form.code || ''} onChange={(e) => onChange('code', e.target.value)} />
              <TextField label="Name" value={form.name || ''} onChange={(e) => onChange('name', e.target.value)} />
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currency || 'AUD'}
                  label="Currency"
                  onChange={(e) => onChange('currency', e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <PriceField
                label="Price"
                value={form.price}
                onChange={(v) => onChange('price', v)}
                currency={form.currency || 'AUD'}
              />
              
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.type || 'other'}
                  label="Type"
                  onChange={(e) => onChange('type', e.target.value)}
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField label="Quantity" type="number" value={form.quantity ?? ''} onChange={(e) => onChange('quantity', e.target.value === '' ? null : Number(e.target.value))} />
              <FormControlLabel control={<Switch checked={!!form.isActive} onChange={(e) => onChange('isActive', e.target.checked)} />} label="Active" />
              <StripeCatalogField
                kind="addon"
                value={form.stripePriceId}
                onChange={(v) => onChange('stripePriceId', v)}
                amount={Number(form.price ?? 0)}
                currency={form.currency || 'AUD'}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={createModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitCreate} disabled={createMutation.isLoading || !form.code || !form.name}>
              {createMutation.isLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit dialog */}
        <Dialog open={editModal.isOpen} onClose={editModal.close} maxWidth="md" fullWidth>
          <DialogTitle>Edit Addon Plan</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
              <TextField label="Code" value={form.code || ''} onChange={(e) => onChange('code', e.target.value)} />
              <TextField label="Name" value={form.name || ''} onChange={(e) => onChange('name', e.target.value)} />
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currency || 'AUD'}
                  label="Currency"
                  onChange={(e) => onChange('currency', e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <PriceField
                label="Price"
                value={form.price}
                onChange={(v) => onChange('price', v)}
                currency={form.currency || 'AUD'}
              />
              
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.type || 'other'}
                  label="Type"
                  onChange={(e) => onChange('type', e.target.value)}
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField label="Quantity" type="number" value={form.quantity ?? ''} onChange={(e) => onChange('quantity', e.target.value === '' ? null : Number(e.target.value))} />
              <FormControlLabel control={<Switch checked={!!form.isActive} onChange={(e) => onChange('isActive', e.target.checked)} />} label="Active" />
              <StripeCatalogField
                kind="addon"
                value={form.stripePriceId}
                onChange={(v) => onChange('stripePriceId', v)}
                amount={Number(form.price ?? 0)}
                currency={form.currency || 'AUD'}
                savedPriceId={editingAddon?.stripePriceId}
                savedProductId={editingAddon?.stripeProductId}
                willReprice={willReprice}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={editModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitUpdate} disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status toggle warning dialog */}
        <Dialog
          open={toggleConfirmModal.isOpen}
          onClose={toggleConfirmModal.close}
          PaperProps={{
            sx: {
              borderRadius: '20px',
              padding: '32px 24px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              maxWidth: '440px',
              width: '100%',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 1 }}>
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                background: (toggleConfirmModal.data as SubscriptionAddonDto)?.isActive
                  ? 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)'
                  : 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                color: (toggleConfirmModal.data as SubscriptionAddonDto)?.isActive ? '#e53935' : '#43a047',
                boxShadow: (toggleConfirmModal.data as SubscriptionAddonDto)?.isActive
                  ? '0 8px 20px rgba(229, 57, 53, 0.15)'
                  : '0 8px 20px rgba(67, 160, 71, 0.15)',
              }}
            >
              {(toggleConfirmModal.data as SubscriptionAddonDto)?.isActive ? (
                <WarningAmberOutlined sx={{ fontSize: 32 }} />
              ) : (
                <CheckCircleOutlined sx={{ fontSize: 32 }} />
              )}
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
              {(toggleConfirmModal.data as SubscriptionAddonDto)?.isActive ? 'Deactivate Addon?' : 'Activate Addon?'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.6, fontSize: '0.975rem' }}>
              Are you sure you want to {(toggleConfirmModal.data as SubscriptionAddonDto)?.isActive ? 'deactivate' : 'activate'} the addon <strong>{(toggleConfirmModal.data as SubscriptionAddonDto)?.name}</strong>?
            </Typography>
          </Box>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 3, px: 0, pb: 0 }}>
            <Button
              onClick={toggleConfirmModal.close}
              sx={{
                borderRadius: '10px',
                px: 3,
                py: 1,
                color: '#64748b',
                border: '1px solid #cbd5e1',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '100px',
                '&:hover': {
                  background: '#f8fafc',
                  borderColor: '#94a3b8',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmToggleActive}
              disabled={updateMutation.isLoading}
              sx={{
                borderRadius: '10px',
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '100px',
                background: (toggleConfirmModal.data as SubscriptionAddonDto)?.isActive
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: (toggleConfirmModal.data as SubscriptionAddonDto)?.isActive
                  ? '0 4px 14px rgba(239, 68, 68, 0.3)'
                  : '0 4px 14px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: (toggleConfirmModal.data as SubscriptionAddonDto)?.isActive
                    ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                    : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Addons;
